import { useCallback, useContext, useEffect } from 'react';

import { getApolloClient } from '@confluence/graphql/entry-points/getApolloClient';
import { useSubscriptionEvent } from '@confluence/graphql/entry-points/useSubscriptionEvent';
import { usePubSubEvent } from '@confluence/pubsub-client/entry-points/usePubSubEvent';
import { RoutesContext } from '@confluence/route-manager/entry-points/RoutesContext';
import { fg } from '@confluence/feature-gating';
import { StatsigConfigurations } from '@confluence/statsig-client/entry-points/StatsigConfigurations';

import { useEditorAnalyticsEvent } from '../useEditorAnalyticsEvent';

import { ContentSpaceKeyQuery } from './ContentSpaceKeyQuery.graphql';
import type {
	ContentSpaceKeyQuery as ContentSpaceKeyQueryType,
	ContentSpaceKeyQueryVariables,
} from './__types__/ContentSpaceKeyQuery';

export const useSpaceKeyWatcher = ({ contentType }: { contentType: string }) => {
	const { match, replace, toUrl } = useContext(RoutesContext);
	const { contentId, spaceKey: spaceKeyFromUrl } = match?.params ?? {};

	const { triggerAnalyticsEvent } = useEditorAnalyticsEvent({
		contentId,
		contentType,
		source: 'editPageScreen',
		subject: 'editor',
		collabService: 'ncs',
	});

	// Replaces the URL to match the given space key
	const changeUrl = useCallback(
		(spaceKey: string) => {
			// Only changing the space key
			const params = {
				...match?.params,
				query: match?.query,
				spaceKey,
			};

			const url = toUrl(match?.name, params);
			if (url !== null) {
				replace(url);
			}
		},
		[replace, toUrl, match],
	);

	// Changes the URL if the new space key from the move event is different from the current
	const handleMoveEvent = useCallback(
		(_: any, payload: any) => {
			if (
				!['DUAL', 'GRAPHQL'].includes(
					StatsigConfigurations.getString('confluence-migrate-fps-to-graphql-subscription', ''),
				) ||
				!fg('cc_fps_migration_page-moved')
			) {
				const newSpaceAlias = payload?.movePageRequestBody?.newSpaceAlias ?? null;
				if (
					newSpaceAlias !== null &&
					newSpaceAlias?.toLowerCase() !== spaceKeyFromUrl?.toLowerCase()
				) {
					changeUrl(newSpaceAlias);
				}

				// Fire analytics to track when the page or blog was moved
				const eventName = contentType === 'page' ? 'pageMoved' : 'blogMoved';

				void triggerAnalyticsEvent(eventName, {
					editorType: 'next',
					...payload,
				});
			}
		},
		[changeUrl, contentType, spaceKeyFromUrl, triggerAnalyticsEvent],
	);

	// Changes the URL if the new space key from the move event is different from the current
	const onMoveSubscriptionEvent = useCallback(
		(response: any) => {
			const newSpaceAlias = response?.pageMoved?.newSpaceAlias ?? null;

			if (
				newSpaceAlias !== null &&
				newSpaceAlias?.toLowerCase() !== spaceKeyFromUrl?.toLowerCase()
			) {
				changeUrl(newSpaceAlias);
			}

			// Fire analytics to track when the page or blog was moved
			const eventName = contentType === 'page' ? 'pageMoved' : 'blogMoved';
			void triggerAnalyticsEvent(eventName, {
				editorType: 'next',
				...response,
			});
		},
		[changeUrl, contentType, spaceKeyFromUrl, triggerAnalyticsEvent],
	);

	// Handles the case where the page is moved while the user is editing it
	usePubSubEvent({
		contentId,
		contentType,
		eventName: 'moved:page',
		onEvent: handleMoveEvent,
		skip:
			// eslint-disable-next-line confluence-feature-gating/no-preconditioning
			fg('cc_fps_migration_page-moved') &&
			fg('confluence_enable_subscriptions-context-provider') &&
			StatsigConfigurations.getString('confluence-migrate-fps-to-graphql-subscription', '') ===
				'GRAPHQL',
	});

	// Handles the case where the page is moved while the user is editing it
	useSubscriptionEvent({
		contentId,
		contentType,
		subscriberId: 'UseSpaceKeyWatcherSubscription',
		delta: 'pageMoved',
		onDataReceived: onMoveSubscriptionEvent,
		skip:
			// eslint-disable-next-line confluence-feature-gating/no-preconditioning
			!fg('cc_fps_migration_page-moved') ||
			!fg('confluence_enable_subscriptions-context-provider') ||
			!['DUAL', 'GRAPHQL'].includes(
				StatsigConfigurations.getString('confluence-migrate-fps-to-graphql-subscription', ''),
			),
	});

	// Handles the case where the user returns to a bookmarked page which has since been moved
	useEffect(() => {
		const fetchSpaceKey = async () => {
			// eslint-disable-next-line graphql-relay-compat/no-direct-apollo-query-and-mutation-operations -- Read http://go/cfe-no-direct-apollo-query-and-mutation-operations
			const { data } = await getApolloClient().query<
				ContentSpaceKeyQueryType,
				ContentSpaceKeyQueryVariables
			>({
				query: ContentSpaceKeyQuery,
				variables: {
					contentId,
				},
				fetchPolicy: 'network-only',
			});

			const contentSpaceKeyAsKey = data?.content?.nodes?.[0]?.space?.key ?? null;
			const contentSpaceAliasAsKey = data?.content?.nodes?.[0]?.space?.alias ?? null;
			const contentSpaceKey = contentSpaceAliasAsKey || contentSpaceKeyAsKey;
			if (contentSpaceKey !== null && contentSpaceKey !== spaceKeyFromUrl) {
				changeUrl(contentSpaceKey);
			}
		};

		if (contentId) {
			void fetchSpaceKey();
		}
	}, [contentId, changeUrl, spaceKeyFromUrl]);
};
